import Tabs from '../../../components/AnimatedTabs';
import { ageLabel } from '../../../utils/records';
import Table from "../../../components/RecordTable";
import { safeDocumentUrl, careDate } from "../../../utils/records";
import useAutoRefresh from "../../../hooks/useAutoRefresh";
import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Typography,

  Row,
  Col,
  Statistic,
  Descriptions,
  Timeline,
  Badge,
  Tooltip,
  Divider,
  Empty,
  Alert,
} from "antd";
import {
  FileTextOutlined,
  UploadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  HomeOutlined,
  FileSearchOutlined,
  DollarOutlined,
  CalendarOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import API from "../../../services/api";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Search } = Input;

const TestBookings = () => {
  const { labId } = useParams();
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({
    all: 0,
    pending: 0,
    confirmed: 0,
    sample_collection_scheduled: 0,
    sample_collected: 0,
    sample_testing: 0,
    completed: 0,
    cancelled: 0,
    refunded: 0,
  });

  // Pagination & Filters
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalBookings, setTotalBookings] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [bookingTypeFilter, setBookingTypeFilter] = useState("");

  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [assignTestsModalVisible, setAssignTestsModalVisible] = useState(false);

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedTests, setSelectedTests] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [labPartners, setLabPartners] = useState([]);

  const [statusForm] = Form.useForm();
  const [reportForm] = Form.useForm();
  const [assignTestsForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("all");

  useAutoRefresh(() => fetchBookings());

  useEffect(() => {
    fetchBookings();
    fetchAvailableTests();
    fetchLabPartners();
  }, [labId, currentPage, pageSize, searchText, statusFilter, bookingTypeFilter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = {
        page: currentPage,
        labAdminId: labId,
        limit: pageSize,
      };

      if (searchText) params.search = searchText;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (bookingTypeFilter && bookingTypeFilter !== "all")
        params.bookingType = bookingTypeFilter;

      const response = await API.testBooking.getByLabAdmin(params);

      const data = response;

      setBookings(data.data || []);
      setTotalBookings(data.total || 0);
      setTotalPages(data.pages || 0);
      setStats(data.statusCounts || stats);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error(
        error.response?.data?.message || "Failed to fetch bookings"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTests = async () => {
    try {
      const response = await API.labTest.getAll();
      setAvailableTests(response.data || []);
    } catch (error) {
      console.error("Error fetching tests:", error);
    }
  };

  const fetchLabPartners = async () => {
    try {
      const response = await API.labPartner.getAll(labId);
      setLabPartners(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Error fetching lab partners:", error);
    }
  };

  const handleStatusUpdate = async (values) => {
    try {
      setLoading(true);
      await API.testBooking.updateStatus(selectedBooking._id, {
        status: values.status,
        notes: values.notes,
        updatedBy: labId,
      });
      message.success("Status updated successfully");
      setStatusModalVisible(false);
      statusForm.resetFields();
      fetchBookings();
    } catch (error) {
      console.error("Error updating status:", error);
      message.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleSampleCollected = async (bookingId) => {
    try {
      setLoading(true);
      await API.testBooking.markSampleCollected(bookingId, {
        collectedBy: "Lab Staff",
        notes: "Sample collected successfully",
      });
      message.success("Sample marked as collected");
      fetchBookings();
    } catch (error) {
      console.error("Error marking sample collected:", error);
      message.error(
        error.response?.data?.message || "Failed to mark sample collected"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUploadReport = async (values) => {
    try {
      setLoading(true);

      const reportUrl =
        values.report?.file?.response?.url || "https://example.com/report.pdf";

      await API.testBooking.uploadReport(selectedBooking._id, {
        reportUrl,
        reportName: values.report?.file?.name || "Test Report",
        uploadedBy: labId,
        remarks: values.remarks,
      });

      message.success("Report uploaded successfully");
      setReportModalVisible(false);
      reportForm.resetFields();
      fetchBookings();
    } catch (error) {
      console.error("Error uploading report:", error);
      message.error(error.response?.data?.message || "Failed to upload report");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTests = async (values) => {
    try {
      setLoading(true);

      // Get test details with pricing
      const testsWithPricing = selectedTests.map((testId) => {
        const test = availableTests.find((t) => t._id === testId);
        return {
          test: testId,
          testName: test?.name || "Unknown Test",
          testId: testId,
          price: test?.minPrice || test?.price || 0,
          discountedPrice: test?.minPrice || test?.price || 0,
          labName:
            labPartners.find((p) => p._id === values.labPartner)?.labName || "",
        };
      });

      // Calculate total amount
      const totalAmount = testsWithPricing.reduce(
        (sum, test) => sum + (test.discountedPrice || 0),
        0
      );

      await API.testBooking.assignTests(selectedBooking._id, {
        tests: testsWithPricing,
        labPartner: values.labPartner,
        labAdmin: labId,
        totalAmount: totalAmount,
        totalTests: testsWithPricing.length,
        updatedBy: labId,
      });

      message.success("Tests assigned successfully");
      setAssignTestsModalVisible(false);
      assignTestsForm.resetFields();
      setSelectedTests([]);
      fetchBookings();
    } catch (error) {
      console.error("Error assigning tests:", error);
      message.error(error.response?.data?.message || "Failed to assign tests");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleBookingTypeFilterChange = (value) => {
    setBookingTypeFilter(value);
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "orange",
      confirmed: "blue",
      sample_collection_scheduled: "cyan",
      sample_collected: "purple",
      sample_testing: "geekblue",
      completed: "green",
      cancelled: "red",
      refunded: "volcano",
    };
    return colors[status] || "default";
  };

  const getStatusText = (status) => {
    const texts = {
      pending: "Pending",
      confirmed: "Confirmed",
      sample_collection_scheduled: "Collection Scheduled",
      sample_collected: "Sample Collected",
      sample_testing: "Testing",
      completed: "Completed",
      cancelled: "Cancelled",
      refunded: "Refunded",
    };
    return texts[status] || status;
  };

  const getBookingTypeText = (type) => {
    return type === "instant_query" ? "Instant Query" : "Regular";
  };

  const bookingColumns = [
    {
      title: "Booking #",
      dataIndex: "bookingNumber",
      key: "bookingNumber",
      fixed: "left",
      width: 130,
      render: (text) => (
        <Text strong style={{ color: "#0A6EBD" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "bookingType",
      key: "bookingType",
      width: 130,
      render: (type) => (
        <Tag color={type === "instant_query" ? "purple" : "blue"}>
          {getBookingTypeText(type)}
        </Tag>
      ),
    },
    {
      title: "Patient",
      dataIndex: "patient",
      key: "patient",
      width: 180,
      render: (patient) => (
        <div>
          <div style={{ fontWeight: 500 }}>{patient?.name || "N/A"}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {patient?.phone || ""}
          </Text>
        </div>
      ),
    },
    {
      title: "Tests",
      dataIndex: "tests",
      key: "tests",
      width: 200,
      render: (tests, record) => {
        if (
          record.bookingType === "instant_query" &&
          (!tests || tests.length === 0)
        ) {
          return (
            <Tag color="warning">
              <FileSearchOutlined /> Pending Assignment
            </Tag>
          );
        }
        return (
          <div>
            <Badge
              count={tests?.length || 0}
              style={{ backgroundColor: "#0A6EBD" }}
            >
              <Tag icon={<ExperimentOutlined />} color="cyan">
                Tests
              </Tag>
            </Badge>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              {tests?.slice(0, 2).map((t, i) => (
                <div key={i}>{t.testName}</div>
              ))}
              {tests?.length > 2 && (
                <Text type="secondary">+{tests.length - 2} more</Text>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Lab",
      dataIndex: "labAdmin",
      key: "labAdmin",
      width: 150,
      render: (labAdmin, record) => {
        if (record.bookingType === "instant_query" && !labAdmin) {
          return <Tag color="default">Not Assigned</Tag>;
        }
        return labAdmin?.labName || "N/A";
      },
    },
    {
      title: "Amount",
      key: "amount",
      width: 120,
      render: (_, record) => (
        <div>
          <Text strong>₹{record.totalAmount || 0}</Text>
          {record.paymentMethod && (
            <>
              <br />
              <Tag color="success" style={{ fontSize: 10 }}>
                {record.paymentMethod}
              </Tag>
            </>
          )}
        </div>
      ),
    },
    {
      title: "Home Collection",
      dataIndex: "homeCollection",
      key: "homeCollection",
      width: 130,
      render: (homeCollection) =>
        homeCollection?.required ? (
          <Tag icon={<HomeOutlined />} color="green">
            Yes (₹{homeCollection.charge})
          </Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    },
    {
      title: "Sample",
      key: "sample",
      width: 120,
      render: (_, record) => {
        if (record.bookingType === "instant_query" && !record.tests?.length) {
          return <Tag color="default">N/A</Tag>;
        }
        const collected = record.sampleCollection?.collected;
        return (
          <Tag color={collected ? "success" : "warning"}>
            {collected ? "Collected" : "Pending"}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: "Booked on",
      dataIndex: "bookingDate",
      key: "bookingDate",
      width: 120,
      render: (date) => careDate(date),
    },
    {
      title: "Preferred Slot",
      key: "preferredSlot",
      width: 130,
      render: (_, r) => r.preferredDate ? (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600 }}>{careDate(r.preferredDate)}</div>
          {r.preferredTimeSlot && <div style={{ color: "#1890ff" }}>{r.preferredTimeSlot}</div>}
        </div>
      ) : <span style={{ color: "#aaa" }}>—</span>,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space orientation="vertical" size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedBooking(record);
              setDetailModalVisible(true);
            }}
          >
            View Details
          </Button>

          {/* Show Assign Tests button for instant queries */}
          {record.bookingType === "instant_query" &&
            (!record.tests || record.tests.length === 0) && (
              <Button
                type="primary"
                size="small"
                icon={<FileSearchOutlined />}
                onClick={() => {
                  setSelectedBooking(record);
                  setAssignTestsModalVisible(true);
                }}
              >
                Assign Tests
              </Button>
            )}

          {/* Show Mark Sample Collected for regular bookings */}
          {record.bookingType === "regular" &&
            record.tests?.length > 0 &&
            !record.sampleCollection?.collected &&
            record.status !== "cancelled" && (
              <Button
                type="link"
                size="small"
                onClick={() => handleSampleCollected(record._id)}
              >
                Mark Sample Collected
              </Button>
            )}

          {/* Update Status - Available for all bookings */}
          {record.status !== "cancelled" && record.status !== "completed" && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setSelectedBooking(record);
                statusForm.setFieldsValue({ status: record.status });
                setStatusModalVisible(true);
              }}
            >
              Update Status
            </Button>
          )}

          {/* Upload Report */}
          {record.status === "sample_testing" && (
            <Button
              type="primary"
              size="small"
              icon={<UploadOutlined />}
              onClick={() => {
                setSelectedBooking(record);
                setReportModalVisible(true);
              }}
            >
              Upload Report
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Filter bookings by tab
  const getFilteredBookings = () => {
    switch (activeTab) {
      case "instant_queries":
        return bookings.filter((b) => b.bookingType === "instant_query");
      case "sample_collection":
        return bookings.filter(
          (b) =>
            b.status === "sample_collection_scheduled" ||
            b.status === "confirmed"
        );
      case "testing":
        return bookings.filter((b) => b.status === "sample_testing");
      case "completed":
        return bookings.filter((b) => b.status === "completed");
      default:
        return bookings;
    }
  };

  return (
    <div>
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Bookings"
              value={stats.all || 0}
              prefix={<ExperimentOutlined />}
              styles={{ content: { color: "#0A6EBD" } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending"
              value={stats.pending || 0}
              prefix={<ClockCircleOutlined />}
              styles={{ content: { color: "#faad14" } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Completed"
              value={stats.completed || 0}
              prefix={<CheckCircleOutlined />}
              styles={{ content: { color: "#0A6EBD" } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Testing"
              value={stats.sample_testing || 0}
              prefix={<ExperimentOutlined />}
              styles={{ content: { color: "#1890ff" } }}
            />
          </Card>
        </Col>
      </Row>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: "#0A6EBD", margin: 0 }}>
          Test Bookings Management
        </Title>
        <Text type="secondary">
          Manage all test bookings and instant queries
        </Text>
      </div>

      {/* Filters & Search */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search by booking #, patient name, phone..."
              allowClear
              enterButton
              onSearch={handleSearch}
              loading={loading}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by Status"
              allowClear
              onChange={handleStatusFilterChange}
              value={statusFilter || undefined}
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="confirmed">Confirmed</Option>
              <Option value="sample_collection_scheduled">
                Collection Scheduled
              </Option>
              <Option value="sample_collected">Sample Collected</Option>
              <Option value="sample_testing">Testing</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by Type"
              allowClear
              onChange={handleBookingTypeFilterChange}
              value={bookingTypeFilter || undefined}
            >
              <Option value="all">All Types</Option>
              <Option value="regular">Regular</Option>
              <Option value="instant_query">Instant Query</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} style={{ textAlign: "right" }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchBookings}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <Badge count={stats.all} offset={[10, 0]} overflowCount={999}>
                All Bookings
              </Badge>
            }
            key="all"
          >
            <Table
              columns={bookingColumns}
              dataSource={bookings}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 1800 }}
              onChange={handleTableChange}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalBookings,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} bookings`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <Badge
                count={
                  bookings.filter((b) => b.bookingType === "instant_query")
                    .length
                }
                offset={[10, 0]}
              >
                Instant Queries
              </Badge>
            }
            key="instant_queries"
          >
            <Table
              columns={bookingColumns}
              dataSource={bookings.filter(
                (b) => b.bookingType === "instant_query"
              )}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 1800 }}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} instant queries`,
              }}
            />
          </TabPane>

          <TabPane tab="Sample Collection" key="sample_collection">
            <Table
              columns={bookingColumns}
              dataSource={bookings.filter(
                (b) =>
                  b.status === "sample_collection_scheduled" ||
                  b.status === "confirmed"
              )}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 1800 }}
            />
          </TabPane>

          <TabPane tab="Testing" key="testing">
            <Table
              columns={bookingColumns}
              dataSource={bookings.filter((b) => b.status === "sample_testing")}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 1800 }}
            />
          </TabPane>

          <TabPane tab="Completed" key="completed">
            <Table
              columns={bookingColumns}
              dataSource={bookings.filter((b) => b.status === "completed")}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 1800 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Status Update Modal */}
      <Modal
        title="Update Booking Status"
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          statusForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleStatusUpdate}>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <Select size="large">
              <Option value="pending">Pending</Option>
              <Option value="confirmed">Confirmed</Option>
              <Option value="sample_collection_scheduled">
                Collection Scheduled
              </Option>
              <Option value="sample_collected">Sample Collected</Option>
              <Option value="sample_testing">Testing</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="refunded">Refunded</Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Add any notes" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setStatusModalVisible(false);
                  statusForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Status
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Report Modal */}
      <Modal
        title="Upload Test Report"
        open={reportModalVisible}
        onCancel={() => {
          setReportModalVisible(false);
          reportForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={reportForm} layout="vertical" onFinish={handleUploadReport}>
          <Form.Item
            name="report"
            label="Upload Report (PDF)"
            rules={[{ required: true, message: "Please upload report" }]}
          >
            <Upload accept=".pdf" maxCount={1} beforeUpload={() => false}>
              <Button icon={<UploadOutlined />} size="large" block>
                Select PDF Report
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item name="remarks" label="Remarks">
            <TextArea
              rows={3}
              placeholder="Any additional notes about the report"
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setReportModalVisible(false);
                  reportForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Upload Report
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Tests Modal (for Instant Queries) */}
      <Modal
        title="Assign Tests to Patient"
        open={assignTestsModalVisible}
        onCancel={() => {
          setAssignTestsModalVisible(false);
          assignTestsForm.resetFields();
          setSelectedTests([]);
        }}
        footer={null}
        width={700}
      >
        <Form
          form={assignTestsForm}
          layout="vertical"
          onFinish={handleAssignTests}
        >
          <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Patient" span={3}>
              {selectedBooking?.patient?.name}
            </Descriptions.Item>
<Descriptions.Item label="Age">{ageLabel(selectedBooking?.patient)}</Descriptions.Item>
            <Descriptions.Item label="Phone" span={3}>
              {selectedBooking?.patient?.phone}
            </Descriptions.Item>
            <Descriptions.Item label="Email" span={3}>
              {selectedBooking?.patient?.email}
            </Descriptions.Item>
            {selectedBooking?.prescriptionUrl && (
              <Descriptions.Item label="Prescription" span={3}>
                <Button
                  type="link"
                  icon={<FileTextOutlined />}
                  onClick={() =>
                    window.open(safeDocumentUrl(selectedBooking.prescriptionUrl), "_blank", "noopener,noreferrer")
                  }
                >
                  View Prescription
                </Button>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Form.Item
            name="tests"
            label="Select Tests"
            rules={[
              { required: true, message: "Please select at least one test" },
            ]}
          >
            <Select
              mode="multiple"
              size="large"
              placeholder="Select tests"
              onChange={setSelectedTests}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {availableTests.map((test) => (
                <Option key={test._id} value={test._id}>
                  {test.name} - ₹{test.minPrice || test.price || 0}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="labPartner" label="Lab Partner (Optional)">
            <Select size="large" placeholder="Select lab partner" allowClear>
              {labPartners.map((partner) => (
                <Option key={partner._id} value={partner._id}>
                  {partner.labName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedTests.length > 0 && (
            <Alert
              message={`Total Amount: ₹${selectedTests.reduce((sum, testId) => {
                const test = availableTests.find((t) => t._id === testId);
                return sum + (test?.minPrice || test?.price || 0);
              }, 0)}`}
              type="info"
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setAssignTestsModalVisible(false);
                  assignTestsForm.resetFields();
                  setSelectedTests([]);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Assign Tests
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Booking Details Modal */}
      <Modal
        title="Booking Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedBooking && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Booking Number" span={2}>
                <Text strong style={{ color: "#0A6EBD" }}>
                  {selectedBooking.bookingNumber}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Booking Type" span={2}>
                <Tag
                  color={
                    selectedBooking.bookingType === "instant_query"
                      ? "purple"
                      : "blue"
                  }
                >
                  {getBookingTypeText(selectedBooking.bookingType)}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Patient Name">
                {selectedBooking.patient?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Contact">
                {selectedBooking.patient?.phone}
              </Descriptions.Item>

              {selectedBooking.patient?.email && (
                <Descriptions.Item label="Email" span={2}>
                  {selectedBooking.patient.email}
                </Descriptions.Item>
              )}

              {selectedBooking.labAdmin && (
                <Descriptions.Item label="Lab" span={2}>
                  {selectedBooking.labAdmin?.labName || "N/A"}
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Total Amount">
                ₹{selectedBooking.totalAmount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {selectedBooking.paymentMethod || "N/A"}
              </Descriptions.Item>

              <Descriptions.Item label="Booked on" span={2}>
                {careDate(selectedBooking.bookingDate || selectedBooking.createdAt, true)}
              </Descriptions.Item>

              {selectedBooking.appointmentDate && (
                <Descriptions.Item label="Appointment Date" span={2}>
                  {careDate(selectedBooking.appointmentDate)}
                </Descriptions.Item>
              )}

              {selectedBooking.preferredDate && (
                <Descriptions.Item label="Preferred Date" span={2}>
                  {careDate(selectedBooking.preferredDate)}
                </Descriptions.Item>
              )}
              {selectedBooking.preferredTimeSlot && (
                <Descriptions.Item label="Preferred Time Slot (IST)" span={2}>
                  <Tag color="blue">{selectedBooking.preferredTimeSlot}</Tag>
                </Descriptions.Item>
              )}

              {selectedBooking.homeCollection?.required && (
                <>
                  <Descriptions.Item label="Home Collection" span={2}>
                    <Tag icon={<HomeOutlined />} color="green">
                      Yes (₹{selectedBooking.homeCollection.charge})
                    </Tag>
                  </Descriptions.Item>
                  {selectedBooking.homeCollection.address && (
                    <Descriptions.Item label="Address" span={2}>
                      {selectedBooking.homeCollection.address}
                    </Descriptions.Item>
                  )}
                </>
              )}

              {selectedBooking.prescriptionUrl && (
                <Descriptions.Item label="Prescription" span={2}>
                  <Button
                    type="link"
                    icon={<FileTextOutlined />}
                    onClick={() =>
                      window.open(safeDocumentUrl(selectedBooking.prescriptionUrl), "_blank", "noopener,noreferrer")
                    }
                  >
                    View Prescription
                  </Button>
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedBooking.tests && selectedBooking.tests.length > 0 && (
              <>
                <Divider>Tests Ordered</Divider>
                <Table
                  size="small"
                  dataSource={selectedBooking.tests}
                  rowKey={(record, index) =>
                    record.testId || record._id || index
                  }
                  pagination={false}
                  columns={[
                    {
                      title: "Test Name",
                      dataIndex: "testName",
                      key: "testName",
                    },
                    {
                      title: "Price",
                      dataIndex: "price",
                      key: "price",
                      render: (price) => `₹${price || 0}`,
                    },
                    {
                      title: "Discounted",
                      dataIndex: "discountedPrice",
                      key: "discountedPrice",
                      render: (price) => `₹${price || 0}`,
                    },
                  ]}
                />
              </>
            )}

            {selectedBooking.statusHistory?.length > 0 && (
              <>
                <Divider>Status History</Divider>
                <Timeline>
                  {selectedBooking.statusHistory.map((history, index) => (
                    <Timeline.Item
                      key={index}
                      color={getStatusColor(history.status)}
                    >
                      <Text strong>{getStatusText(history.status)}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {careDate(history.timestamp, true)}
                      </Text>
                      {history.notes && (
                        <>
                          <br />
                          <Text style={{ fontSize: 12 }}>{history.notes}</Text>
                        </>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </>
            )}

            {selectedBooking.reports?.length > 0 && (
              <>
                <Divider>Reports</Divider>
                {selectedBooking.reports.map((report, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 8 }}>
                    <Space>
                      <FileTextOutlined style={{ fontSize: 20 }} />
                      <div>
                        <div>{report.reportName || "Test Report"}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {careDate(report.uploadedAt, true)}
                        </Text>
                      </div>
                      <Button
                        type="link"
                        onClick={() => window.open(safeDocumentUrl(report.reportUrl), "_blank", "noopener,noreferrer")}
                      >
                        View Report
                      </Button>
                    </Space>
                  </Card>
                ))}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TestBookings;
